import { useCallback, useEffect, useRef } from 'react'
import { driver, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import '@/styles/driverjs-theme.css'
import { useAuthStore } from '@/store/authStore'
import {
  markTourSeen,
  resetTour as resetTourState,
  isTourSeen,
  isAutoTourSuppressed,
  suppressAutoTours,
  resetAutoTourSuppression,
} from '@/services/tourState'
import {
  getTourById,
  getTourForPath,
  mapToDriveSteps,
  TOUR_IDS,
  type TourId,
} from '@/services/tours/tourCatalog'

interface StartTourOptions {
  /** Marca el tour como visto al terminar o cerrar. Por defecto true. */
  markSeen?: boolean
}

const SKIP_BUTTON_CLASS = 'iridia-tour-skip'

/**
 * Orquesta las guías interactivas con Driver.js v1.
 *
 * - `startTour(id)` lanza un tour concreto (siempre manual).
 * - `startTourForPath(pathname)` lanza el tour de la sección actual.
 * - La guía del panel se ofrece sola una vez; el disparo vive en `TourAutoStart`.
 */
export function useTours() {
  const tenantId = useAuthStore((s) => s.tenantId)
  const driverRef = useRef<Driver | null>(null)
  const unmountingRef = useRef(false)

  useEffect(() => {
    unmountingRef.current = false
    return () => {
      unmountingRef.current = true
      const active = driverRef.current
      driverRef.current = null
      if (active && active.isActive()) {
        active.destroy()
      }
    }
  }, [])

  const startTour = useCallback(
    (id: TourId, options?: StartTourOptions) => {
      const definition = getTourById(id)
      if (!definition || !tenantId) return false

      const current = driverRef.current
      if (current && current.isActive()) {
        unmountingRef.current = true
        current.destroy()
        unmountingRef.current = false
      }

      const instance = driver({
        showProgress: true,
        progressText: '{{current}} de {{total}}',
        nextBtnText: 'Siguiente',
        prevBtnText: 'Anterior',
        doneBtnText: 'Entendido',
        allowClose: true,
        allowKeyboardControl: true,
        smoothScroll: true,
        animate: true,
        // El overlay oscuro deja ver con claridad el hueco que marca el paso;
        // el anillo de foco se dibuja sobre ese hueco desde driverjs-theme.css.
        overlayColor: '#0b1f1a',
        overlayOpacity: 0.55,
        stagePadding: 8,
        stageRadius: 10,
        popoverOffset: 12,
        popoverClass: 'iridia-driver-popover',
        steps: mapToDriveSteps(definition.steps),
        onPopoverRendered: (popover) => {
          // Solo la guía automática ofrece "Omitir": en las manuales basta la X
          // porque el cliente las abrió a propósito.
          if (!definition.autoStart) return
          const footer = popover.footer
          if (!footer || footer.querySelector(`.${SKIP_BUTTON_CLASS}`)) return

          const skip = document.createElement('button')
          skip.type = 'button'
          skip.className = SKIP_BUTTON_CLASS
          skip.textContent = 'Omitir guía'
          skip.setAttribute('aria-label', 'Omitir la guía interactiva y no volver a mostrarla sola')
          skip.addEventListener('click', () => {
            suppressAutoTours(tenantId)
            driverRef.current?.destroy()
          })
          footer.insertBefore(skip, footer.firstChild)
        },
        onDestroyed: () => {
          if (!unmountingRef.current && options?.markSeen !== false) {
            markTourSeen(tenantId, id)
          }
          if (driverRef.current === instance) {
            driverRef.current = null
          }
        },
      })

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('iridia:expand-sidebar'))
      }

      driverRef.current = instance
      instance.drive()
      return true
    },
    [tenantId]
  )

  const startTourForPath = useCallback(
    (pathname: string, options?: StartTourOptions) => {
      const definition = getTourForPath(pathname)
      if (!definition) return false
      return startTour(definition.id, options)
    },
    [startTour]
  )

  /** Reinicia un tour para que vuelva a ofrecerse en la próxima visita. */
  const resetTour = useCallback(
    (id: TourId) => {
      resetTourState(tenantId, id)
    },
    [tenantId]
  )

  /** Indica si el tour ya fue visto por este tenant. */
  const hasSeenTour = useCallback((id: TourId) => isTourSeen(tenantId, id), [tenantId])

  /** La guía automática fue omitida: no se vuelve a abrir sola. */
  const autoTourSuppressed = useCallback(() => isAutoTourSuppressed(tenantId), [tenantId])

  /** Restablece todos los tours (incluida la omisión) para volver a mostrarlos. */
  const resetAllTours = useCallback(() => {
    TOUR_IDS.forEach((id) => resetTourState(tenantId, id))
    resetAutoTourSuppression(tenantId)
  }, [tenantId])

  return {
    startTour,
    startTourForPath,
    resetTour,
    resetAllTours,
    hasSeenTour,
    autoTourSuppressed,
  }
}
